import '../loading-button/loading-button'
import { GMXLoadingButton } from '../loading-button/loading-button'

const STAGGER_DELAY_MS = 50
const SCROLL_ANIMATION_DURATION_MS = 300

/**
 * Load More Results Component
 *
 * A component that wraps a list of results and provides "load more" functionality
 * with staggered animations and scroll behavior.
 *
 * @example
 * <gmx-load-more-results data-initial-visible="10" data-load-more-count="5">
 *   <div class="gmx-load-more-results-container">
 *     <ul class="gmx-text-list layout-stacked style-title-desc">
 *       <li>Result 1</li>
 *       <li>Result 2</li>
 *       ...
 *     </ul>
 *   </div>
 *   <div class="gmx-load-more-btn-container">
 *     <gmx-loading-btn>
 *       <button type="button" class="gmx-btn gmx-btn-secondary gmx-loading-btn">
 *         <span class="loading-btn-text">Load more</span>
 *         <span class="loading-btn-loading">Loading<span class="loading-btn-ellipsis"></span></span>
 *       </button>
 *     </gmx-loading-btn>
 *   </div>
 * </gmx-load-more-results>
 *
 * @fires load-more-requested - Fired when load more is clicked (for async data fetching)
 * @fires load-more-complete - Fired after items are revealed
 * @fires all-items-shown - Fired when all items have been revealed
 */
export class GMXLoadMoreResults extends HTMLElement {
	private loadingBtn: GMXLoadingButton | null = null
	private resultItems: HTMLElement[] = []
	private currentlyVisible = 0
	private isInitialized = false

	/** Number of items to show initially */
	get initialVisible(): number {
		return parseInt(this.getAttribute('data-initial-visible') || '10', 10)
	}
	set initialVisible(val: number) {
		this.setAttribute('data-initial-visible', String(val))
	}

	/** Number of items to reveal on each "load more" click */
	get loadMoreCount(): number {
		return parseInt(this.getAttribute('data-load-more-count') || '5', 10)
	}
	set loadMoreCount(val: number) {
		this.setAttribute('data-load-more-count', String(val))
	}

	connectedCallback(): void {
		if (this.isInitialized) return
		this.isInitialized = true
		this.setupLoadingButton()
		this.collectResultItems()
		this.showInitialItems()
	}

	disconnectedCallback(): void {
		if (this.loadingBtn) {
			this.loadingBtn.removeEventListener('loading-btn-click', this.handleLoadMoreClick)
		}
	}

	private setupLoadingButton(): void {
		this.loadingBtn = this.querySelector('gmx-loading-btn') as GMXLoadingButton | null
		if (this.loadingBtn) {
			this.loadingBtn.addEventListener('loading-btn-click', this.handleLoadMoreClick)
		}
	}

	private collectResultItems(): void {
		// Find list items to manage within an optional results container wrapper
		const container = (this.querySelector('.gmx-load-more-results-container') as HTMLElement) || this
		const listItems = container.querySelectorAll('li, [data-result]')
		this.resultItems = Array.from(listItems) as HTMLElement[]

		// Initially hide all items
		this.resultItems.forEach(item => {
			item.classList.add('gmx-load-more-item')
			item.classList.remove('is-visible')
		})
	}

	private showInitialItems(): void {
		const count = Math.min(this.initialVisible, this.resultItems.length)
		this.revealItems(0, count)
		this.updateButtonVisibility()
	}

	private handleLoadMoreClick = (): void => {
		if (!this.loadingBtn) return

		// Dispatch event for async data fetching scenarios
		const event = new CustomEvent('load-more-requested', {
			bubbles: true,
			composed: true,
			detail: {
				currentlyVisible: this.currentlyVisible,
				totalItems: this.resultItems.length,
			},
		})
		this.dispatchEvent(event)

		// Start loading state
		this.loadingBtn.startLoading()

		// Reveal more items after a brief delay (simulating load)
		setTimeout(() => {
			this.showMoreItems()
			if (this.loadingBtn) {
				this.loadingBtn.stopLoading()
			}
		}, 500)
	}

	/**
	 * Show more items (called internally or can be called externally for async scenarios)
	 */
	showMoreItems(): void {
		const startIndex = this.currentlyVisible
		const endIndex = Math.min(startIndex + this.loadMoreCount, this.resultItems.length)

		this.revealItems(startIndex, endIndex)
		this.updateButtonVisibility()

		// Dispatch completion event
		this.dispatchEvent(
			new CustomEvent('load-more-complete', {
				bubbles: true,
				composed: true,
				detail: {
					newlyVisible: endIndex - startIndex,
					totalVisible: this.currentlyVisible,
					totalItems: this.resultItems.length,
				},
			})
		)
	}

	private revealItems(startIndex: number, endIndex: number): void {
		const newItemsCount = endIndex - startIndex
		let lastRevealedItem: HTMLElement | null = null

		for (let i = startIndex; i < endIndex; i += 1) {
			const item = this.resultItems[i]
			if (item) {
				const delay = (i - startIndex) * STAGGER_DELAY_MS
				setTimeout(() => {
					item.classList.add('is-visible')
				}, delay)
				lastRevealedItem = item
			}
		}

		this.currentlyVisible = endIndex

		// Scroll to the last new item after animations complete
		if (lastRevealedItem && startIndex > 0) {
			const scrollDelay = newItemsCount * STAGGER_DELAY_MS + SCROLL_ANIMATION_DURATION_MS
			setTimeout(() => {
				lastRevealedItem?.scrollIntoView({ behavior: 'smooth', block: 'end' })
			}, scrollDelay)
		}
	}

	private updateButtonVisibility(): void {
		if (!this.loadingBtn) return

		const allShown = this.currentlyVisible >= this.resultItems.length

		if (allShown) {
			this.loadingBtn.style.display = 'none'
			this.dispatchEvent(
				new CustomEvent('all-items-shown', {
					bubbles: true,
					composed: true,
					detail: { totalItems: this.resultItems.length },
				})
			)
		} else {
			this.loadingBtn.style.display = ''
		}
	}

	/**
	 * Reset the component to initial state
	 */
	reset(): void {
		this.currentlyVisible = 0
		this.resultItems.forEach(item => {
			item.classList.remove('is-visible')
		})
		this.showInitialItems()
	}

	/**
	 * Refresh the list of items (call after dynamically adding items)
	 */
	refresh(): void {
		const previouslyVisible = this.currentlyVisible
		this.collectResultItems()

		// Show items up to the previous count or initial visible, whichever is greater
		const targetVisible = Math.max(previouslyVisible, this.initialVisible)
		const count = Math.min(targetVisible, this.resultItems.length)

		// Immediately show items that should be visible (no animation on refresh)
		for (let i = 0; i < count; i += 1) {
			const item = this.resultItems[i]
			if (item) {
				item.classList.add('is-visible')
			}
		}
		this.currentlyVisible = count

		this.updateButtonVisibility()
	}

	/**
	 * Get the loading button element for external control
	 */
	getLoadingButton(): GMXLoadingButton | null {
		return this.loadingBtn
	}
}

if (!customElements.get('gmx-load-more-results')) {
	customElements.define('gmx-load-more-results', GMXLoadMoreResults)
}
